// @ts-strict-ignore
import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import Link from 'antd/lib/typography/Link';
import { AppRouteComponentMatchParams, FeatureSection, ReleaseComponentProps } from '../../typescriptHelpers';
import { useTrackPageView } from '../../helpers/trackingHelpers';
import { useParams } from 'react-router-dom';
import {
    FeatureFlag,
    FeatureFlagStatus,
    FeatureFlagType,
    IntegrationType,
    IntegrationOptimizely,
    IntegrationCapability,
    IntegrationLaunchDarkly,
    IntegrationStatsig,
    IntegrationAmplitudeExperiment,
    IntegrationUnleash,
    IntegrationFirebaseFeatureFlagging,
    ReleaseNav,
} from '../../types';
import PageLayout from '../SettingsPageLayout';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectConnectedCapabilitiesForApp,
    selectEffectiveTimezone,
    selectIntegrationForCapability,
    selectIntegrationFromIntegrationType,
    selectReleaseVersion,
    selectIsDemoInstanceOrDemoModeEnabled,
    selectShouldShowFeatureFlags,
    selectIsWorkflowsCIStepEnabled,
} from '../../selectors/selectors';
import { RootState } from '../../reducers/reducers';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { StarFilled } from '@ant-design/icons';
import { dayjs } from '@/extendedDayjs';
import Table, { ColumnsType } from 'antd/lib/table';
import { DatetimeFormat, getLocaleDateTimeFormat } from '../../timeHelpers';
import rotatedArrow from '../../../assets/rotated-arrow-filled.svg';
import FeatureFlagsSortComponent from './FeatureFlagSortComponent';
import { selectSortedFeatureFlags } from '../../selectors/memoizedSelectors';
import { tryFetchEntity } from '../../actions/entities';
import { FetchableEntityType } from '../../entityType';
import { AlignType } from 'rc-table/lib/interface';
import { amplitudeExperimentDeploymentLabels, iconAndAltForIntegrationType } from '../../helpers/integrationHelpers';
import FeatureFlagsIntegrationBanner from './FeatureFlagsIntegrationBanner';
import { globalMessage } from '../../utils/globalMessage';
import { trackReleaseRouteEvent } from '../../actions/trackEvent';
import * as AnalyticEvents from '../../utils/analytics';
import FeatureFlagDrawer from './FeatureFlagDrawer';
import { FeatureFlagStatusTag } from './FeatureFlagStatus';
import { EmptyStateWrapper } from '../releaseEmptyState/EmptyStateWrapper';
import { EmptyStepBanner } from '../releaseEmptyState/EmptyStateBanner';
import Space from 'antd/lib/space';
import Tooltip from 'antd/lib/tooltip';
import useSetLastViewedReleaseStep from '../hooks/useSetLastViewedReleaseStep';
import Search from 'antd/lib/input/Search';
import { useError, useIsLoading } from '@/utils/hooks/helpers';

const FeatureFlags: React.FC<ReleaseComponentProps> = (props: ReleaseComponentProps) => {
    const { releaseId, appId, orgId } = props;
    const { appIdParam } = useParams<AppRouteComponentMatchParams>();
    const dispatch = useDispatch();
    const [drawerVisible, setDrawerVisible] = React.useState(false);
    const [selectedFeatureFlag, setSelectedFeatureFlag] = React.useState<FeatureFlag | null>(null);
    const [searchValue, setSearchValue] = React.useState('');

    const featureFlagIntegrationId = useSelector(
        (state: RootState) =>
            selectIntegrationForCapability(state, props.appId, IntegrationCapability.FeatureFlagging)?.id,
    );

    const integration = useSelector((state: RootState) =>
        selectIntegrationForCapability(state, props.appId, IntegrationCapability.FeatureFlagging),
    );

    const featureFlagIntegrationEnvironment = (() => {
        switch (integration?.id) {
            case IntegrationType.Optimizely:
                return (integration as IntegrationOptimizely).data?.selectedEnvironment?.name;
            case IntegrationType.LaunchDarkly:
                return (integration as IntegrationLaunchDarkly).data?.selectedEnvironment?.name;
            case IntegrationType.Statsig:
                return (integration as IntegrationStatsig).data?.selectedEnvironment?.name;
            case IntegrationType.AmplitudeExperiment:
                return amplitudeExperimentDeploymentLabels(integration as IntegrationAmplitudeExperiment);
            case IntegrationType.Unleash:
                return (integration as IntegrationUnleash).data?.selectedEnvironment?.name;
            default:
                return null;
        }
    })();

    const featureFlagIntegrationProject = (() => {
        switch (integration?.id) {
            case IntegrationType.Optimizely:
                return (integration as IntegrationOptimizely).data?.selectedProject?.name;
            case IntegrationType.LaunchDarkly:
                return (integration as IntegrationLaunchDarkly).data?.selectedProject?.name;
            case IntegrationType.Statsig: {
                return (integration as IntegrationStatsig).data?.selectedProject?.name;
            }
            case IntegrationType.AmplitudeExperiment: {
                // Amplitude only exposes numeric project ids, so label it to make the number recognizable
                const projectId = (integration as IntegrationAmplitudeExperiment).data?.selectedProject?.id;
                return projectId ? `Project ${projectId}` : null;
            }
            case IntegrationType.Unleash:
                return (integration as IntegrationUnleash).data?.selectedProject?.name;
            case IntegrationType.FirebaseFeatureFlagging:
                return (integration as IntegrationFirebaseFeatureFlagging).data?.projectId;
            default:
                return null;
        }
    })();

    const hasFeatureFlagIntegrationConnected = useSelector((state: RootState) => {
        const integration = selectIntegrationForCapability(state, props.appId, IntegrationCapability.FeatureFlagging);

        return !!integration;
    });

    const isDemoMode = useSelector((state: RootState) => selectIsDemoInstanceOrDemoModeEnabled(state, props.orgId));

    const shouldFetchData = !isDemoMode;

    const shouldFetchFeatureFlags = shouldFetchData && hasFeatureFlagIntegrationConnected;

    const currentVersion = useSelector((state: RootState) => selectReleaseVersion(state, props.releaseId));
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, props.appId));

    const hasFeatureFlagCapability = useSelector((state: RootState) =>
        selectConnectedCapabilitiesForApp(state, props.appId).includes(IntegrationCapability.FeatureFlagging),
    );
    const showConnectIntegrationsBanner = !hasFeatureFlagCapability;

    const isLoadingFeatureFlags = useIsLoading(FetchableEntityType.FeatureFlags, releaseId);
    const loadFeatureFlagsError = useError(FetchableEntityType.FeatureFlags, releaseId);

    const sortedFeatureFlags = useSelector((state: RootState) => selectSortedFeatureFlags(state, props.releaseId));
    const [filteredFlags, setFilteredFlags] = React.useState<FeatureFlag[]>(sortedFeatureFlags);

    React.useEffect(() => {
        if (searchValue === '') {
            setFilteredFlags(sortedFeatureFlags);
        } else {
            const filteredFlags = sortedFeatureFlags.filter((flag) =>
                flag.name.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase()),
            );

            setFilteredFlags(filteredFlags);
        }
    }, [searchValue, sortedFeatureFlags]);

    let isUsingOptimizely = false;

    const optimizely = useSelector((state: RootState) => {
        return selectIntegrationFromIntegrationType(state, props.appId, IntegrationType.Optimizely);
    }) as IntegrationOptimizely;
    const shouldShowFeatureFlags = useSelector((state: RootState) =>
        selectShouldShowFeatureFlags(state, props.orgId, props.appId),
    );

    if (optimizely) {
        isUsingOptimizely = true;
    }

    let isUsingLaunchDarkly = false;
    const launchDarkly = useSelector((state: RootState) => {
        return selectIntegrationFromIntegrationType(state, props.appId, IntegrationType.LaunchDarkly);
    }) as IntegrationLaunchDarkly;

    if (launchDarkly) {
        isUsingLaunchDarkly = true;
    }

    // Remote Config has no per-parameter owner or creation date, and its type split (flag vs config row) is
    // already conveyed by whether the toggle is available
    const isUsingFirebaseRemoteConfig = integration?.id === IntegrationType.FirebaseFeatureFlagging;

    const renderTitle = (title: string) => {
        return (
            <div>
                <Text style={{ fontSize: 12, color: antGrey.grey7, fontWeight: 400 }}>{title}</Text>
            </div>
        );
    };

    const renderTableText = (
        text: string,
        enabled: boolean,
        fontWeight?: number,
        width?: number,
        hideTooltip?: boolean,
    ) => {
        return (
            <Text
                style={{
                    color: enabled ? antGrey.grey8 : antGrey.grey6,
                    fontWeight: fontWeight ?? 400,
                    maxWidth: width ?? '100%',
                }}
                ellipsis={hideTooltip ? true : { tooltip: { title: text } }}
            >
                {text}
            </Text>
        );
    };

    const getFeatureFlagTypeDisplayName = (type?: FeatureFlagType): string => {
        if (!type) {
            return 'Unknown';
        }
        switch (type) {
            case FeatureFlagType.OptimizelyExperiment:
                return 'Experiment';
            case FeatureFlagType.OptimizelyFeature:
                return 'Feature';
            case FeatureFlagType.OptimizelyFlag:
                return 'Flag';
            case FeatureFlagType.LaunchDarklyFlag:
                return 'Flag';
            case FeatureFlagType.StatsigGate:
                return 'Gate';
            case FeatureFlagType.StatsigDynamicConfig:
                return 'Dynamic Config';
            case FeatureFlagType.FirebaseRemoteConfigParam:
                return 'Parameter';
            case FeatureFlagType.FirebaseRemoteConfigBoolParam:
                return 'Flag';
            case FeatureFlagType.AmplitudeExperimentFlag:
                return 'Flag';
            case FeatureFlagType.AmplitudeExperimentExperiment:
                return 'Experiment';
            case FeatureFlagType.UnleashFeature:
                return 'Flag';
            default:
                return 'Unknown';
        }
    };

    const columns: ColumnsType<FeatureFlag> = [
        {
            title: renderTitle('Flag name'),
            render: (featureFlag: FeatureFlag) => {
                return (
                    <Space>
                        <FeatureFlagIndicator featureFlag={featureFlag} />
                        {!featureFlag.url &&
                            renderTableText(
                                featureFlag.name,
                                featureFlag.status === FeatureFlagStatus.Enabled,
                                null,
                                220,
                            )}
                        {featureFlag.url && (
                            <Space className="feature-flag-hover" align="center">
                                {renderTableText(
                                    featureFlag.name,
                                    featureFlag.status === FeatureFlagStatus.Enabled,
                                    null,
                                    220,
                                )}
                                <Link href={featureFlag.url} target="_blank" onClick={(e) => e.stopPropagation()}>
                                    <div
                                        className="hide"
                                        style={{
                                            width: 18,
                                            height: 18,
                                            borderRadius: 4,
                                            backgroundColor: runwayColors.primary,
                                            fontSize: 12,
                                        }}
                                    >
                                        <img
                                            src={rotatedArrow}
                                            height={10}
                                            width={10}
                                            style={{
                                                position: 'absolute',
                                                marginTop: 4,
                                                marginLeft: 4,
                                                color: 'white',
                                            }}
                                        />
                                    </div>
                                </Link>
                            </Space>
                        )}
                    </Space>
                );
            },
            width: 300,
        },
        {
            title: renderTitle('Rules'),
            ellipsis: true,
            render: (featureFlag: FeatureFlag) => {
                const rules = featureFlag.rules.map((rule) => {
                    const name = rule.name;
                    const audienceConditionTexts = rule.audienceConditions?.map((condition) => {
                        const negation = condition.negate ? 'not ' : '';
                        if (condition.audiences?.length === 1) {
                            return `${negation}${condition.audiences[0].name}, ${condition.weight}%`;
                        }
                        if (condition.audiences) {
                            const audienceNames = condition.audiences?.map((audience) => audience.name);
                            return `${negation}${audienceNames.join(', ')}: ${condition.weight}%`;
                        } else {
                            return '';
                        }
                    });
                    if (audienceConditionTexts?.length) {
                        return `${name} (${audienceConditionTexts.join(', ')})`;
                    }
                    return name;
                });

                const columnText = renderTableText(
                    rules.join(', '),
                    featureFlag.status === FeatureFlagStatus.Enabled,
                    null,
                    null,
                    true,
                );

                const tooltipRules = featureFlag.rules.map((rule) => {
                    const variationTitle = rule.variations.length === 1 ? 'Variation' : 'Variations';
                    const variationsText = rule.variations
                        .map((variation) => {
                            if (variation.weight === 100) {
                                return variation.name;
                            }
                            return `${variation.name} (${variation.weight}%)`;
                        })
                        .join(', ');
                    return (
                        <div key={rule.id} style={{ display: 'flex', flexDirection: 'column', marginBottom: 6 }}>
                            <div>Rule name: {rule.name}</div>
                            {rule.audienceConditions?.map((condition, idx) => {
                                const negation = condition.negate ? 'not ' : '';
                                const key = `${rule.id}-${idx}`;
                                if (condition.audiences) {
                                    const audienceNames = condition.audiences?.map((audience) => audience.name);
                                    return (
                                        <div key={key}>
                                            Serving:{' '}
                                            {`${condition.weight}% of users ${negation}matching ${audienceNames.join(
                                                ', ',
                                            )}`}
                                        </div>
                                    );
                                } else {
                                    return <></>;
                                }
                            })}
                            <div>
                                {variationTitle}: {variationsText}
                            </div>
                        </div>
                    );
                });

                return <Tooltip title={<Space direction="vertical">{tooltipRules}</Space>}>{columnText}</Tooltip>;
            },
        },
        {
            title: renderTitle('Type'),
            hidden: isUsingLaunchDarkly || isUsingFirebaseRemoteConfig,
            ellipsis: true,
            render: (featureFlag: FeatureFlag) => {
                return renderTableText(
                    getFeatureFlagTypeDisplayName(featureFlag.type),
                    featureFlag.status === FeatureFlagStatus.Enabled,
                );
            },
            width: 150,
        },
        {
            title: renderTitle('Owner'),
            hidden: isUsingOptimizely || isUsingFirebaseRemoteConfig,
            ellipsis: true,
            render: (featureFlag: FeatureFlag) => {
                return renderTableText(featureFlag.owner ?? '', featureFlag.status === FeatureFlagStatus.Enabled);
            },
            width: 150,
        },
        {
            title: renderTitle('Created on'),
            hidden: isUsingFirebaseRemoteConfig,
            width: 118,
            render: (featureFlag: FeatureFlag) => {
                return (
                    <Text>
                        {renderTableText(
                            dayjs(featureFlag.createdAt)
                                .tz(effectiveTimezone)
                                .format(getLocaleDateTimeFormat('dateOnly', false)),
                            featureFlag.status === FeatureFlagStatus.Enabled,
                        )}
                    </Text>
                );
            },
        },
        {
            title: renderTitle('Min ver'),
            align: 'center' as AlignType,
            width: 86,
            render: (featureFlag: FeatureFlag) => {
                let fontWeight = 400;
                if (featureFlag.minimumVersion == currentVersion) {
                    fontWeight = 600;
                }
                return renderTableText(
                    featureFlag.minimumVersion ?? '',
                    featureFlag.status === FeatureFlagStatus.Enabled,
                    fontWeight,
                );
            },
        },
        {
            title: renderTitle('Status'),
            width: 110,
            render: (featureFlag: FeatureFlag) => {
                return (
                    <div>
                        {featureFlag.status == FeatureFlagStatus.Enabled && featureFlag.enabledAt ?
                            <Tooltip
                                title={`Enabled on: ${dayjs(featureFlag.enabledAt)
                                    .tz(effectiveTimezone)
                                    .format(DatetimeFormat.ShortMonthDateYear)}`}
                            >
                                <FeatureFlagStatusTag status={featureFlag.status} integrationType={integration?.id} />
                            </Tooltip>
                        : featureFlag.status == FeatureFlagStatus.Disabled && featureFlag.estimatedDisabledAt ?
                            <Tooltip
                                title={`Disabled on: ${dayjs(featureFlag.estimatedDisabledAt)
                                    .tz(effectiveTimezone)
                                    .format(DatetimeFormat.ShortMonthDateYear)}`}
                            >
                                <FeatureFlagStatusTag status={featureFlag.status} integrationType={integration?.id} />
                            </Tooltip>
                        :   <FeatureFlagStatusTag status={featureFlag.status} integrationType={integration?.id} />}
                    </div>
                );
            },
        },
    ].filter((item) => !item.hidden);

    const [integrationIcon, integrationIconAlt] = iconAndAltForIntegrationType(featureFlagIntegrationId);

    React.useEffect(() => {
        if (loadFeatureFlagsError) {
            globalMessage.error(loadFeatureFlagsError);
        }
    }, [loadFeatureFlagsError]);

    React.useEffect(() => {
        if (selectedFeatureFlag) {
            // If the selected feature flag is updated (status toggled), update the state used for the Drawer
            const updatedFlag = sortedFeatureFlags.find((flag) => flag.id === selectedFeatureFlag.id);
            if (updatedFlag && updatedFlag.status !== selectedFeatureFlag.status) {
                setSelectedFeatureFlag(updatedFlag);
            }
        }
    }, [sortedFeatureFlags, selectedFeatureFlag?.id]);

    React.useEffect(() => {
        if (shouldFetchFeatureFlags) {
            dispatch(tryFetchEntity(FetchableEntityType.FeatureFlags, { orgId, appId, releaseId }, releaseId));
        }
    }, [releaseId, shouldFetchFeatureFlags]);

    useTrackPageView('Feature flags', [appIdParam]);

    React.useEffect(() => {
        dispatch(
            trackReleaseRouteEvent({
                orgId: orgId,
                appId: appId,
                releaseId: releaseId,
                eventName: AnalyticEvents.Event.ViewFeatureFlags,
            }),
        );
    }, []);

    useSetLastViewedReleaseStep(releaseId, ReleaseNav.FeatureFlags);

    return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            {showConnectIntegrationsBanner && <FeatureFlagsIntegrationBanner {...props} />}
            {hasFeatureFlagIntegrationConnected && shouldShowFeatureFlags && (
                <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                        <Search
                            placeholder="Search feature flags"
                            allowClear
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            onSearch={setSearchValue}
                            style={{ width: 400 }}
                        />
                        <Space>
                            <Space size={8} align="center">
                                {integrationIcon && (
                                    <img src={integrationIcon} alt={integrationIconAlt} width={21} height={21} />
                                )}
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'baseline',
                                        gap: 4,
                                        lineHeight: '32px',
                                        fontSize: 14,
                                    }}
                                >
                                    {featureFlagIntegrationProject && (
                                        <Text style={{ fontWeight: 600, color: antGrey.grey8, whiteSpace: 'nowrap' }}>
                                            {featureFlagIntegrationProject}
                                        </Text>
                                    )}
                                    {featureFlagIntegrationEnvironment && (
                                        <>
                                            <Text type="secondary">-</Text>
                                            <Text
                                                type="secondary"
                                                style={{ maxWidth: 360 }}
                                                ellipsis={{ tooltip: featureFlagIntegrationEnvironment }}
                                            >
                                                {featureFlagIntegrationEnvironment}
                                            </Text>
                                        </>
                                    )}
                                </div>
                            </Space>
                            <FeatureFlagsSortComponent />
                        </Space>
                    </div>
                    <EmptyStateWrapper
                        appId={appId}
                        featureSections={[FeatureSection.FeatureFlagsTable]}
                        releaseId={releaseId}
                        shouldCenterContent
                        shouldForceDisable={sortedFeatureFlags.length > 0}
                    >
                        <div>
                            <Table
                                dataSource={filteredFlags}
                                columns={columns}
                                className="feature-flag-table"
                                pagination={{ hideOnSinglePage: true, defaultPageSize: 10 }}
                                style={{ minWidth: 900, cursor: 'pointer' }}
                                rowKey="id"
                                loading={isLoadingFeatureFlags}
                                onRow={(clickedFlag) => {
                                    return {
                                        onClick: () => {
                                            setDrawerVisible(true);
                                            setSelectedFeatureFlag(clickedFlag);
                                        },
                                    };
                                }}
                            />
                        </div>
                    </EmptyStateWrapper>
                    {!!integration && !!selectedFeatureFlag && (
                        <FeatureFlagDrawer
                            integrationId={integration.id}
                            integrationName={integration.name}
                            projectName={featureFlagIntegrationProject}
                            environmentName={featureFlagIntegrationEnvironment}
                            currentVersion={currentVersion}
                            integrationIcon={integrationIcon}
                            integrationIconAlt={integrationIconAlt}
                            featureFlag={selectedFeatureFlag}
                            onClose={() => {
                                setDrawerVisible(false);
                            }}
                            open={drawerVisible}
                            {...props}
                        ></FeatureFlagDrawer>
                    )}
                </>
            )}
        </div>
    );
};

interface FeatureFlagBadgeProps {
    tooltipText: string;
    backgroundColor: string;
    icon: React.ReactNode;
}

const FeatureFlagBadge: React.FC<FeatureFlagBadgeProps> = ({ tooltipText, backgroundColor, icon }) => {
    return (
        <div style={{ width: 18, height: 18, borderRadius: 4, backgroundColor, fontSize: 12 }}>
            <Tooltip title={tooltipText}>{icon}</Tooltip>
        </div>
    );
};

export const FeatureFlagStar: React.FC<{ tooltipText: string }> = ({ tooltipText }) => (
    <FeatureFlagBadge
        tooltipText={tooltipText}
        backgroundColor={runwayColors.primary}
        icon={<StarFilled style={{ color: 'white', position: 'relative', marginTop: 3, marginLeft: 3 }} />}
    />
);

// Drawn rather than the font glyph so the stem tapers and the dot reads at 18px. Kept as a plain element
// (not a component) so the Tooltip can attach its ref and hover handlers.
const staleExclamationIcon = (
    <svg width={18} height={18} viewBox="0 0 18 18" style={{ display: 'block' }}>
        <path d="M7.1 3.8 H10.9 L9.9 10.4 H8.1 Z" fill="white" />
        <circle cx={9} cy={13.1} r={1.35} fill="white" />
    </svg>
);

export const FeatureFlagStaleBadge: React.FC<{ tooltipText: string }> = ({ tooltipText }) => (
    <FeatureFlagBadge tooltipText={tooltipText} backgroundColor={antGrey.grey7} icon={staleExclamationIcon} />
);

// Highlighting (relevant to this release) wins over staleness
export const FeatureFlagIndicator: React.FC<{ featureFlag: FeatureFlag }> = ({ featureFlag }) => {
    if (featureFlag.shouldHighlight) {
        return <FeatureFlagStar tooltipText={featureFlag.highlightTooltipText} />;
    }
    if (featureFlag.isStale) {
        return <FeatureFlagStaleBadge tooltipText={featureFlag.staleTooltipText} />;
    }
    return null;
};

const FeatureFlagsContainer: React.FC<ReleaseComponentProps> = (props: ReleaseComponentProps) => {
    const isWorkflowsCIStepEnabled = useSelector((state: RootState) =>
        selectIsWorkflowsCIStepEnabled(state, props.appId),
    );

    return (
        <PageLayout
            component={FeatureFlags}
            title="Feature flags"
            description="All feature flags created before this version was released."
            banners={
                <EmptyStepBanner
                    appId={props.appId}
                    releaseId={props.releaseId}
                    releaseStepType={null}
                    customMessage={`The Feature flags page surfaces feature flags and their key attributes, including status, variations, and more.`}
                    isWorkflowsCIStepEnabled={isWorkflowsCIStepEnabled}
                />
            }
            {...props}
        />
    );
};

export default FeatureFlagsContainer;
